import { useEffect, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import AuthGate from './components/AuthGate';
import { AuthProvider } from './context/AuthContext';
import NewApiKeyGate from './components/NewApiKeyGate';
import { CurrencyProvider } from './context/CurrencyContext';
import Home from './pages/Home';
import Showcase from './pages/Showcase';
import PublicLayout from './components/PublicLayout';
import DashboardLayout from './components/DashboardLayout';
import ScrollToTop from './components/ScrollToTop';
import WebMcpTools from './webmcp/WebMcpTools';

// Route-level code splitting. Only the landing page (Home) + the shared layouts
// ship in the main bundle; every other page is a separate chunk fetched on
// demand. This keeps the homepage from downloading/parsing the entire app
// (dashboard, marketplace, docs, and 20+ other pages) up front — the main cause
// of slow first load on mobile. Chunks load behind the layout's <Suspense>.
const Docs = lazy(() => import('./pages/Docs'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Blog = lazy(() => import('./pages/Blog'));
const Consulting = lazy(() => import('./pages/Consulting'));
const Papers = lazy(() => import('./pages/Papers'));
const Welcome = lazy(() => import('./pages/Welcome'));
const PaperDetail = lazy(() => import('./pages/PaperDetail'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const SkillPage = lazy(() => import('./pages/SkillPage'));
const GenerateSkill = lazy(() => import('./pages/GenerateSkill'));
const DeveloperApi = lazy(() => import('./pages/DeveloperApi'));
const MySkills = lazy(() => import('./pages/MySkills'));
const MySkillDetail = lazy(() => import('./pages/MySkillDetail'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const ListingDetail = lazy(() => import('./pages/ListingDetail'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Creators = lazy(() => import('./pages/Creators'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const LoretoApi = lazy(() => import('./pages/LoretoApi'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const GitTimeline = lazy(() => import('./pages/GitTimeline'));
const GitExplorer = lazy(() => import('./pages/GitExplorer'));
const SellSkill = lazy(() => import('./pages/SellSkill'));
const MyListings = lazy(() => import('./pages/MyListings'));
const SellerMetrics = lazy(() => import('./pages/SellerMetrics'));
const MyAgents = lazy(() => import('./pages/MyAgents'));
const Library = lazy(() => import('./pages/Library'));
const Messages = lazy(() => import('./pages/Messages'));
const ProfileEdit = lazy(() => import('./pages/ProfileEdit'));

function App() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <ScrollToTop />
            <WebMcpTools />
            <AuthGate />
            <NewApiKeyGate />
            <Routes>
              {/* Public routes share one persistent layout (Navbar/Footer/bg) so
                  navigating between them swaps only the inner page — no flicker. */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/consulting" element={<Consulting />} />
                <Route path="/api" element={<LoretoApi />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/papers" element={<Papers />} />
                <Route path="/papers/:slug" element={<PaperDetail />} />
                <Route path="/skills/:slug" element={<SkillPage />} />
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/marketplace/:slug" element={<ListingDetail />} />
                <Route path="/showcase/:slug" element={<Showcase />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/creators" element={<Creators />} />
                <Route path="/u/:username" element={<PublicProfile />} />
                <Route path="/legal/:slug" element={<LegalPage />} />
                <Route path="/git-timeline" element={<GitTimeline />} />
                <Route path="/git-timeline/explore" element={<GitExplorer />} />
                <Route path="/git-timeline/:owner/:repo" element={<GitExplorer />} />
                <Route path="*" element={<Home />} />
              </Route>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard/generate" element={<GenerateSkill />} />
                <Route path="/dashboard/developer" element={<DeveloperApi />} />
                <Route path="/dashboard/skills" element={<MySkills />} />
                <Route path="/dashboard/skills/:id" element={<MySkillDetail />} />
                <Route path="/dashboard/sell" element={<SellSkill />} />
                <Route path="/dashboard/listings" element={<MyListings />} />
                <Route path="/dashboard/listings/:id/edit" element={<SellSkill />} />
                <Route path="/dashboard/sales" element={<SellerMetrics />} />
                <Route path="/dashboard/agents" element={<MyAgents />} />
                <Route path="/dashboard/library" element={<Library />} />
                <Route path="/dashboard/messages" element={<Messages />} />
                <Route path="/dashboard/profile" element={<ProfileEdit />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </CurrencyProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
